<template>
  <div
    ref="cardRef"
    :class="
      cn(
        'relative flex shrink-0 items-center justify-center size-40 cursor-pointer select-none transition-all duration-200',
        props.class,
      )
    "
    :draggable="props.draggable"
    @dragstart="handleDragStart"
    @mouseenter="isPointerHover = true"
    @mouseleave="isPointerHover = false"
  >
    <!-- 背景图片 -->
    <img
      :src="bgImage"
      class="absolute inset-0 size-full object-contain pointer-events-none"
      alt="configuration-card-bg"
    />

    <!-- 内容区域 -->
    <div class="relative size-full z-index-carousel flex flex-col items-center px-4 py-6">
      <slot name="tags">
        <div
          v-show="isActive || isNew"
          class="w-full flex justify-between items-center text-s-medium min-h-6"
        >
          <Button v-show="isActive" color="text-b-b" size="xs" class="pointer-events-none">
            {{ $t('home.config.card.current') }}
          </Button>
          <span
            v-if="isNew"
            class="absolute top-6 right-3 pr-0.5 font-[Barlow] text-m-medium font-bold uppercase italic bg-linear-to-r from-[#52B7FF] to-[#79FFBE] bg-clip-text text-transparent"
          >
            new
          </span>
          <Icon
            v-else-if="isActive"
            icon="Switch-Fill-Check"
            class="ml-auto size-6 text-brand-blue1"
          />
        </div>
      </slot>

      <div
        class="absolute top-1/2 left-4 right-4 -translate-y-1/2 text-center text-m-medium truncate"
      >
        {{ name }}
      </div>

      <div class="mt-auto flex justify-center items-center gap-2">
        <slot name="actions">
          <template v-for="action in actions" :key="action.icon">
            <Tooltip :content="action.tip" side="bottom">
              <div @click="handleDisabledAction(action, $event)">
                <Button
                  size="icon-xs"
                  color="text-b-b"
                  shape="circle"
                  :disabled="action.disabled"
                  @click="handleAction(action, $event)"
                >
                  <Icon :icon="action.icon" class="size-4.5" />
                </Button>
              </div>
            </Tooltip>
          </template>
        </slot>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, HtmlHTMLAttributes, onBeforeUnmount, ref } from 'vue'
  import { getImageUrl } from '@/utils/assets'
  import { Icon, Button, Tooltip, toast, cn } from '@rapoo/element'
  import { setDragData, setElementDragImage, DragType } from '@/utils/dnd'
  import { useLocaleStore, useUserStore } from '@/stores'
  import { storeToRefs } from 'pinia'

  const useStore = useUserStore()
  const { isLoggedIn } = storeToRefs(useStore)

  // 定义 Props
  interface Props {
    /**
     * 配置ID
     */
    id: string
    /**
     * 配置名称
     */
    name: string
    /**
     * 背景图片的地址前缀
     * 文件默认用 card-default.png card-hover.png card-active.png
     */
    bgImgPrefix?: string
    /**
     * 是否处于激活状态
     */
    isActive?: boolean
    /**
     * 是否显示新增标识 NEW
     */
    isNew?: boolean
    /**
     * 是否显示删除按钮，默认显示
     */
    showDelete?: boolean
    /**
     * 是否显示编辑按钮，默认显示
     */
    showEdit?: boolean
    /**
     * 是否显示编辑按钮，默认显示
     */
    draggable?: boolean
    /**
     * Card样式
     */
    class?: HtmlHTMLAttributes['class']
  }

  const { t } = useLocaleStore()

  const props = withDefaults(defineProps<Props>(), {
    isActive: false,
    isNew: false,
    showDelete: true,
    showEdit: true,
    draggable: true,
    bgImgPrefix: 'header/configurations',
  })

  const emit = defineEmits<{
    (e: 'upload', id: string): void
    (e: 'download', id: string): void
    (e: 'delete', id: string): void
    (e: 'edit', id: string): void
  }>()

  // 鼠标悬停与程序触发的临时悬停状态
  const isPointerHover = ref(false)
  const isTemporaryHover = ref(false)
  const cardRef = ref<HTMLElement | null>(null)
  let temporaryHoverTimer: ReturnType<typeof setTimeout> | null = null

  // 计算背景图片
  const bgImage = computed(() => {
    if (props.isActive) {
      return getImageUrl(`${props.bgImgPrefix}/card-active.png`)
    }
    return isPointerHover.value || isTemporaryHover.value
      ? getImageUrl(`${props.bgImgPrefix}/card-hover.png`)
      : getImageUrl(`${props.bgImgPrefix}/card-default.png`)
  })

  /** 短暂展示 hover 状态，供外部在保存、定位等操作后强调当前卡片。 */
  const highlightTemporarily = (hoverDuration = 3000) => {
    isTemporaryHover.value = true
    if (temporaryHoverTimer) clearTimeout(temporaryHoverTimer)
    temporaryHoverTimer = setTimeout(() => {
      isTemporaryHover.value = false
      temporaryHoverTimer = null
    }, hoverDuration)
  }

  const getElement = () => cardRef.value

  defineExpose({ getElement, highlightTemporarily })

  onBeforeUnmount(() => {
    if (temporaryHoverTimer) clearTimeout(temporaryHoverTimer)
  })

  // 操作按钮
  const actions = computed(() => {
    return [
      {
        icon: 'Function-Wireframe-CloudUpload',
        handler: async () => emit('upload', props.id),
        tip: isLoggedIn.value
          ? t('home.config.card.uploadText')
          : t('home.config.card.notLoggedInUpload'),
        disabled: !isLoggedIn.value,
        stop: true,
        show: props.isActive,
      },
      {
        icon: 'Function-Wireframe-CloudDownload',
        handler: () => emit('download', props.id),
        tip: isLoggedIn.value
          ? t('home.config.card.downloadText')
          : t('home.config.card.notLoggedInDownload'),
        disabled: !isLoggedIn.value,
        stop: true,
        show: props.isActive,
      },
      {
        icon: 'Function-Wireframe-Delete',
        handler: () => emit('delete', props.id),
        tip: t('home.config.card.delete'),
        disabled: false,
        stop: true,
        show: props.id != '0' && props.showDelete,
      },
      {
        icon: 'Writing-Wireframe-Edit',
        handler: () => emit('edit', props.id),
        tip: t('home.config.card.edit'),
        disabled: false,
        stop: false,
        show: props.id != '0' && props.showEdit,
      },
    ].filter((action) => action.show)
  })
  const handleAction = (
    action: { icon: string; handler: () => void; stop: boolean },
    e: MouseEvent,
  ) => {
    if (action.stop) {
      e.stopPropagation()
    }
    action.handler()
  }
  const handleDisabledAction = (
    action: { icon: string; handler: () => void; stop: boolean; disabled: boolean; tip: string },
    e: MouseEvent,
  ) => {
    if (action.disabled) {
      toast.info(action.tip)
      e.stopPropagation()
    }
  }

  /**
   * 处理拖拽开始
   * @param event 拖拽事件
   */
  const handleDragStart = (event: DragEvent) => {
    if (event.dataTransfer) {
      setDragData(event.dataTransfer, DragType.CONFIGURATION, { id: props.id, name: props.name })
      if (cardRef.value) {
        setElementDragImage(event, cardRef.value)
      }
    }
  }
</script>

<style lang="scss" scoped>
  // 可以在这里添加额外的样式微调
</style>
